import React from "react";
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

function SummaryCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, minWidth: 120, flex: "1 1 120px" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 950 }}>{Number(value || 0).toLocaleString("es-AR")}</Typography>
    </Paper>
  );
}

export default function ImportPreviewStep({ preview }) {
  const totals = preview?.totals || {};
  const errors = preview?.errors || [];
  const rows = preview?.rows || [];

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Preview real del backend</Typography>
        <Typography variant="body2" color="text.secondary">
          Esta simulación usa el borrador, mapping, defaults y modo elegidos. Todavía no modifica productos ni stock.
        </Typography>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1.25}>
        <SummaryCard label="Filas" value={totals.rows} />
        <SummaryCard label="A crear" value={totals.created} />
        <SummaryCard label="A actualizar" value={totals.updated} />
        <SummaryCard label="Omitidas" value={totals.skipped} />
        <SummaryCard label="Errores" value={totals.errors} />
      </Stack>

      {preview?.stockPolicy?.message ? <Alert severity="warning">{preview.stockPolicy.message}</Alert> : null}

      {errors.length ? (
        <Box>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>Errores detectados</Typography>
          <TableContainer sx={{ maxHeight: 260, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Fila</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Campo</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Mensaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error, index) => (
                  <TableRow key={`${error.row}-${error.field}-${index}`}>
                    <TableCell>{error.row}</TableCell>
                    <TableCell>{error.field || "—"}</TableCell>
                    <TableCell>{error.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {preview?.errorsTruncated ? (
            <Typography variant="caption" color="text.secondary">Se muestran sólo los primeros errores para mantener la respuesta acotada.</Typography>
          ) : null}
        </Box>
      ) : <Alert severity="success">No se detectaron errores de fila en la vista previa.</Alert>}

      <Box>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>Muestra del resultado</Typography>
        <TableContainer sx={{ maxHeight: 320, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 900 }}>Fila</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Acción</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Precio</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Stock inicial</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.row}>
                  <TableCell>{row.row}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.action === "create" ? "Crear" : row.action === "update" ? "Actualizar" : "Omitir"}
                      color={row.action === "skip" ? "warning" : row.action === "update" ? "info" : "success"}
                    />
                  </TableCell>
                  <TableCell>{row.values?.name || "—"}</TableCell>
                  <TableCell>{row.values?.code || "—"}</TableCell>
                  <TableCell>{row.values?.price ?? "—"}</TableCell>
                  <TableCell>{row.values?.initialStock ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}
